/**
 * Timeout handling utilities
 */

import * as plugins from '../webrequest.plugins.js';

/**
 * Create an AbortController with timeout
 */
export function createTimeoutController(timeoutMs: number): {
  controller: AbortController;
  cleanup: () => void;
  didTimeout: () => boolean;
} {
  const controller = new AbortController();
  let timeoutTriggered = false;
  let timeoutCleaned = false;

  const timeout = new plugins.smartdelay.Timeout(timeoutMs, null);
  timeout.promise.then(() => {
    timeoutTriggered = true;
    controller.abort();
  });

  const cleanup = () => {
    if (timeoutCleaned) {
      return;
    }
    timeoutCleaned = true;
    timeout.cancel();
  };

  return {
    controller,
    cleanup,
    didTimeout: () => timeoutTriggered,
  };
}

const timeoutErrorMarker = Symbol('webrequestTimeoutError');

export const createTimeoutError = (
  url: string,
  timeoutMs: number,
): Error => {
  const error = new Error(
    `Request timeout after ${timeoutMs}ms: ${url}`,
  );
  Object.defineProperty(error, timeoutErrorMarker, {
    configurable: false,
    enumerable: false,
    value: true,
    writable: false,
  });
  return error;
};

export const isWebrequestTimeoutError = (
  errorArg: unknown,
): errorArg is Error =>
  errorArg instanceof Error &&
  Reflect.get(errorArg, timeoutErrorMarker) === true;

/**
 * Execute a native fetch with a per-attempt response-header timeout.
 */
export async function fetchWithTimeout(
  input: string | URL | Request,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const url = input instanceof Request ? input.url : String(input);
  const { controller, cleanup, didTimeout } =
    createTimeoutController(timeoutMs);
  const signal = init.signal
    ? AbortSignal.any([init.signal, controller.signal])
    : controller.signal;

  try {
    const response = await fetch(input, {
      ...init,
      signal,
    });
    cleanup();
    return response;
  } catch (error) {
    cleanup();

    if (didTimeout()) {
      throw createTimeoutError(url, timeoutMs);
    }

    throw error;
  }
}

const normalizeAbortReason = (signal: AbortSignal): Error => {
  if (signal.reason instanceof Error) {
    return signal.reason;
  }
  const error = new Error('The request was aborted');
  error.name = 'AbortError';
  return error;
};

export interface IConsumeJsonWithTimeoutOptions {
  bodyTimeoutMs?: number;
  reportedTimeoutMs?: number;
  signal?: AbortSignal | null;
  url: string;
}

/**
 * Consume a JSON response within an explicit body budget.
 */
export async function consumeJsonWithTimeout<T>(
  response: Response,
  options: IConsumeJsonWithTimeoutOptions,
): Promise<T> {
  if (options.signal?.aborted) {
    const abortError = normalizeAbortReason(options.signal);
    await response.body?.cancel(abortError).catch(() => undefined);
    throw abortError;
  }
  if (
    options.bodyTimeoutMs !== undefined &&
    options.bodyTimeoutMs <= 0
  ) {
    const timeoutError = createTimeoutError(
      options.url,
      options.reportedTimeoutMs ?? options.bodyTimeoutMs,
    );
    await response.body?.cancel(timeoutError).catch(() => undefined);
    throw timeoutError;
  }
  if (options.bodyTimeoutMs === undefined && !options.signal) {
    return (await response.json()) as T;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    return JSON.parse('') as T;
  }

  let terminationError: Error | undefined;
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  let rejectTermination!: (error: Error) => void;
  const terminationPromise = new Promise<never>((_resolve, reject) => {
    rejectTermination = reject;
  });

  const terminate = (error: Error) => {
    if (terminationError) {
      return;
    }
    terminationError = error;
    void reader.cancel(error).catch(() => undefined);
    rejectTermination(error);
  };

  const abortListener = () => {
    if (options.signal) {
      terminate(normalizeAbortReason(options.signal));
    }
  };

  if (options.signal) {
    if (options.signal.aborted) {
      abortListener();
    } else {
      options.signal.addEventListener('abort', abortListener, {
        once: true,
      });
    }
  }

  if (options.bodyTimeoutMs !== undefined) {
    const reportedTimeoutMs =
      options.reportedTimeoutMs ?? options.bodyTimeoutMs;
    timeoutHandle = setTimeout(() => {
      terminate(createTimeoutError(options.url, reportedTimeoutMs));
    }, options.bodyTimeoutMs);
  }

  const readBody = async (): Promise<T> => {
    const decoder = new TextDecoder();
    let jsonText = '';

    while (true) {
      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await reader.read();
      } catch (error) {
        if (terminationError) {
          throw terminationError;
        }
        throw error;
      }

      if (terminationError) {
        throw terminationError;
      }
      if (readResult.done) {
        break;
      }
      jsonText += decoder.decode(readResult.value, { stream: true });
    }

    jsonText += decoder.decode();
    if (terminationError) {
      throw terminationError;
    }
    return JSON.parse(jsonText) as T;
  };

  try {
    return await Promise.race([readBody(), terminationPromise]);
  } finally {
    if (timeoutHandle !== undefined) {
      clearTimeout(timeoutHandle);
    }
    options.signal?.removeEventListener('abort', abortListener);
    try {
      reader.releaseLock();
    } catch {
      // A terminal cancellation owns the pending read until it settles.
    }
  }
}
